import {
    Truck,
    GitBranch,
    FlaskConical,
    Cog,
    Zap,
    Wrench,
    Globe,
    ArrowRight,
    PackageCheck,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useReveal } from "../hooks/useReveal";

const CAT_ICONS = { Truck, GitBranch, FlaskConical, Cog, Zap, Wrench };

const PURCHASING_IMG =
    "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function MarketCard({ market, index }) {
    const { ref, className } = useReveal(0.1);
    return (
        <div
            ref={ref}
            data-testid={`market-${index}`}
            style={{ transitionDelay: `${index * 70}ms` }}
            className={`${className} bg-white border border-ink p-6 lg:p-7 hover:bg-mint-soft transition-colors`}
        >
            <div className="flex items-baseline gap-3">
                <span className="font-display font-bold text-3xl text-mint-hover">
                    {market.n}
                </span>
                <h4 className="font-display font-bold uppercase text-base lg:text-lg tracking-tight text-ink leading-tight">
                    {market.title}
                </h4>
            </div>
            <p className="mt-4 text-sm text-ink-soft leading-relaxed">
                {market.text}
            </p>
        </div>
    );
}

function CategoryItem({ cat, index }) {
    const { ref, className } = useReveal(0.1);
    const Icon = CAT_ICONS[cat.icon] || Wrench;
    return (
        <div
            ref={ref}
            data-testid={`purchasing-cat-${index}`}
            style={{ transitionDelay: `${index * 50}ms` }}
            className={`${className} flex items-center gap-4 p-5 bg-white border border-border hover:border-ink transition-colors group`}
        >
            <span className="w-12 h-12 flex-shrink-0 bg-ink text-mint flex items-center justify-center group-hover:bg-mint group-hover:text-ink transition-colors">
                <Icon size={22} strokeWidth={1.5} />
            </span>
            <span className="font-display text-sm md:text-base uppercase tracking-tight text-ink leading-tight">
                {cat.title}
            </span>
        </div>
    );
}

export default function Purchasing() {
    const { t } = useLang();
    const p = t.purchasing;
    const head = useReveal();
    const promise = useReveal(0.1);
    const go = (id) =>
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="purchasing"
            data-testid="purchasing-section"
            className="py-24 md:py-32 bg-white relative"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div ref={head.ref} className={`${head.className} max-w-4xl`}>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-10 h-px bg-ink" />
                        <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                            {p.kicker}
                        </span>
                    </div>
                    <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
                        {p.title}
                        <br />
                        <span className="relative inline-block">
                            <span className="relative z-10">{p.title2}</span>
                            <span className="absolute -bottom-1 left-0 right-0 h-3 bg-mint -z-0" />
                        </span>
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-ink-soft max-w-3xl">
                        {p.subtitle}
                    </p>
                </div>

                {/* Promise block + image */}
                <div className="mt-16 grid lg:grid-cols-12 gap-10 lg:gap-12 items-stretch">
                    <div
                        ref={promise.ref}
                        className={`${promise.className} lg:col-span-7 bg-ink text-white p-8 md:p-10 lg:p-12 relative overflow-hidden`}
                    >
                        <div className="absolute inset-0 hex-pattern opacity-20 pointer-events-none" />
                        <div className="relative">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="w-10 h-10 bg-mint text-ink flex items-center justify-center">
                                    <PackageCheck size={20} />
                                </span>
                                <span className="font-display text-xs uppercase tracking-[0.3em] text-mint">
                                    {t.nav.purchasing}
                                </span>
                            </div>
                            <h3 className="font-display font-bold uppercase text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight">
                                {p.promiseTitle}
                            </h3>
                            <p className="mt-6 text-white/80 leading-relaxed md:text-lg max-w-xl">
                                {p.promiseText}
                            </p>
                            <button
                                data-testid="purchasing-cta"
                                onClick={() => go("contact")}
                                className="mt-8 btn-mint group"
                            >
                                {p.ctaText}
                                <ArrowRight
                                    size={16}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </button>
                        </div>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="absolute -inset-3 border border-ink hidden sm:block" />
                        <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-mint hidden sm:flex items-center justify-center">
                            <Globe size={28} className="text-ink" strokeWidth={1.5} />
                        </div>
                        <img
                            src={PURCHASING_IMG}
                            alt="Centrale d'achats internationale"
                            className="relative w-full h-full min-h-[320px] object-cover grayscale"
                        />
                    </div>
                </div>

                {/* Markets */}
                <div className="mt-20">
                    <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight text-ink mb-8">
                        {p.marketsTitle}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {p.markets.map((m, i) => (
                            <MarketCard key={m.n} market={m} index={i} />
                        ))}
                    </div>
                </div>

                {/* Categories */}
                <div className="mt-20">
                    <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight text-ink mb-8">
                        {p.categoriesTitle}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {p.categories.map((c, i) => (
                            <CategoryItem key={i} cat={c} index={i} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
